import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, type DrawerProps, Space, Spin, Form, Result } from 'antd';
import { useField } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type LanguagePackOptionDataFragment } from '../../../api/fragments/LanguagePackOptionData';
import { useGetLanguagePackOptionsQuery } from '../../../api/queries/getLanguagePackOptions';
import { useCompany } from '../../contexts/CompanyContextManager';
import { useGenericStateOnPageContext } from '../../contexts/GenericStateOnPageContextManager';
import DrawerWithAutoWidth from '../../DrawerWithAutoWidth';
import TextAreaField from '../TextAreaField';
import { useAddTranslationModal } from './AddTranslationModal';

export type TranslationDrawerProps = Pick<DrawerProps, 'title' | 'visible' | 'onClose'> & {
    name: string;
    languages?: LanguagePackOptionDataFragment[];
    renderField?: (item: TranslationItem, onDelete: () => void) => JSX.Element;
};

const TranslationDrawer = ({
    onClose,
    title,
    visible,
    name,
    languages: languagesFromProps,
    renderField = renderTextAreaField,
}: TranslationDrawerProps) => {
    const { t } = useTranslation(['translationField']);
    const company = useCompany(true);

    const [{ value: translations }, , { setValue }] = useField<{ languagePackId: string; value: string }[]>({
        name,
    });

    const { data, loading } = useGetLanguagePackOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { companyId: company?.id } },
        skip: !!languagesFromProps,
    });

    const languagesFromQuery = data?.languages?.items;

    const allLanguages = useMemo(
        () => languagesFromProps || languagesFromQuery || [],
        [languagesFromProps, languagesFromQuery]
    );

    const remainingLanguages = useMemo(() => {
        if (!translations) {
            return allLanguages;
        }

        // exclude languages already translated
        return allLanguages.filter(
            language => !translations.some(translation => translation.languagePackId === language.id)
        );
    }, [allLanguages, translations]);

    const actions = useMemo(() => {
        const currentTranslations = translations || [];

        return {
            add: (languagePackId: string) => setValue([...currentTranslations, { languagePackId, value: '' }]),
            delete: (index: number) => {
                const newValue = [...currentTranslations];
                newValue.splice(index, 1);
                setValue(newValue);
            },
        };
    }, [setValue, translations]);

    const addModal = useAddTranslationModal(remainingLanguages, actions.add);

    const cleanedTranslations = useMemo((): TranslationItem[] => {
        if (!translations) {
            return [];
        }

        return translations
            .map((translation, index) => {
                const language = allLanguages.find(language => language.id === translation.languagePackId);

                return { ...translation, language, name: `${name}[${index}].value`, index };
            })
            .filter(translation => !!translation.language)
            .toSorted((a, b) => a.language.referenceName.localeCompare(b.language.referenceName));
    }, [translations, allLanguages, name]);
    const content = (
        <Space direction="vertical" style={{ width: '100%' }}>
            {cleanedTranslations.length > 0 && (
                <Form layout="vertical">
                    {cleanedTranslations.map(translation =>
                        renderField(translation, () => actions.delete(translation.index))
                    )}
                </Form>
            )}
            {remainingLanguages.length > 0 && (
                <Button icon={<PlusOutlined />} onClick={addModal.open} type="dashed" block>
                    {t('translationField:addButton')}
                </Button>
            )}
            {addModal.render()}
            {!cleanedTranslations.length && !remainingLanguages.length && (
                <Result status="info" subTitle="No language pack registered in the system yet" />
            )}
        </Space>
    );

    return (
        <DrawerWithAutoWidth onClose={onClose} placement="right" title={title} visible={visible} destroyOnClose>
            <Spin spinning={loading}>{content}</Spin>
        </DrawerWithAutoWidth>
    );
};

export type TranslationItem = {
    languagePackId: string;
    index: number;
    language: LanguagePackOptionDataFragment;
    name: string;
};

export const renderTextAreaField: TranslationDrawerProps['renderField'] = (translation, onDelete) => (
    <TextAreaField
        key={translation.languagePackId}
        addMoreButton={<Button icon={<DeleteOutlined />} onClick={onDelete} size="small" type="text" />}
        label={translation.language.referenceName}
        name={translation.name}
    />
);

export default TranslationDrawer;

export const useTranslationMultipleEntryDrawer = () => {
    const [visible, setVisible] = useState(false);
    const [disclaimerTextId, setDisclaimerTextId] = useState<number>();
    const genericStateOnPageContext = useGenericStateOnPageContext();

    const withContentRefinement = useMemo(() => {
        if (!genericStateOnPageContext) {
            return null;
        }

        const {
            actions: { setIsContentRefinementGloballyDisabled },
        } = genericStateOnPageContext;

        return { disableContentRefinementGlobally: setIsContentRefinementGloballyDisabled };
    }, [genericStateOnPageContext]);

    const actions = useMemo(
        () => ({
            open: (id: number) => {
                setVisible(true);
                setDisclaimerTextId(id);
            },
            close: () => {
                if (withContentRefinement) {
                    const { disableContentRefinementGlobally } = withContentRefinement;
                    if (disableContentRefinementGlobally) {
                        disableContentRefinementGlobally(false);
                    }
                }
                setVisible(false);
            },
        }),
        [withContentRefinement]
    );

    return {
        ...actions,
        render: (props: Pick<TranslationDrawerProps, 'name' | 'title' | 'renderField' | 'languages'>) => (
            <TranslationDrawer
                {...props}
                name={`${props.name}[${disclaimerTextId}].overrides`}
                onClose={actions.close}
                visible={visible}
            />
        ),
    };
};
